/**
 * Tenant Users Panel — basic tenant-scoped user management.
 * List, invite (first user → TenantAdmin enforced server-side), role edit, deactivate.
 * Not a Keycloak/OpenFGA replacement — identity federation stays there;
 * this panel calls the platform's /tenants/:id/users* endpoints.
 * @see Documentation/Specification/04_Processes/application-agnostic/tenant/TenantManagement.md
 */
import { useState, useEffect, useCallback } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, CircularProgress, Button, Avatar, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, Alert, Tooltip,
} from '@mui/material';
import { PersonAdd, Block, Edit } from '@mui/icons-material';
import { fetchApi } from '../../api/fetchApi';

interface Props {
  tenantId: string;
}

interface TenantUser {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  status: string;
  roles: string[];
  keycloakId?: string;
}

const ROLE_OPTIONS = ['TenantAdmin', 'Employee', 'Manager'];

export const TenantUsersPanel = ({ tenantId }: Props) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState('Employee');
  const [inviteSaving, setInviteSaving] = useState(false);
  const [roleEditUser, setRoleEditUser] = useState<TenantUser | null>(null);
  const [roleEditValue, setRoleEditValue] = useState<string[]>([]);
  const [roleSaving, setRoleSaving] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchApi(`/tenants/${tenantId}/users`);
      setUsers(data?.users ?? []);
    } catch {
      notify('Failed to load tenant users', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tenantId, notify]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const isFirstUser = users.length === 0;

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      notify('Email is required', { type: 'warning' });
      return;
    }
    setInviteSaving(true);
    try {
      await fetchApi(`/tenants/${tenantId}/users/invite`, {
        method: 'POST',
        body: JSON.stringify({
          email: inviteEmail.trim(),
          firstName: inviteFirstName.trim() || undefined,
          lastName: inviteLastName.trim() || undefined,
          roles: isFirstUser ? ['TenantAdmin'] : [inviteRole],
        }),
      });
      notify(isFirstUser
        ? 'User invited as TenantAdmin (first user of the tenant)'
        : 'User invited', { type: 'success' });
      setInviteOpen(false);
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setInviteRole('Employee');
      await loadUsers();
    } catch (err: any) {
      notify(err?.message ?? 'Invite failed', { type: 'error' });
    } finally {
      setInviteSaving(false);
    }
  };

  const handleRolesSave = async () => {
    if (!roleEditUser) return;
    setRoleSaving(true);
    try {
      await fetchApi(`/tenants/${tenantId}/users/${roleEditUser._id}/roles`, {
        method: 'PATCH',
        body: JSON.stringify({ roles: roleEditValue }),
      });
      notify('Roles updated', { type: 'success' });
      setRoleEditUser(null);
      await loadUsers();
    } catch (err: any) {
      notify(err?.message ?? 'Role update failed', { type: 'error' });
    } finally {
      setRoleSaving(false);
    }
  };

  const handleDeactivate = async (user: TenantUser) => {
    if (!window.confirm(`Deactivate ${user.email}? Their account will be suspended.`)) return;
    setDeactivatingId(user._id);
    try {
      await fetchApi(`/tenants/${tenantId}/users/${user._id}/deactivate`, { method: 'POST' });
      notify(`${user.email} deactivated`, { type: 'success' });
      await loadUsers();
      refresh();
    } catch (err: any) {
      notify(err?.message ?? 'Deactivation failed', { type: 'error' });
    } finally {
      setDeactivatingId(null);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>Tenant Users</Typography>
        <Button variant="contained" size="small" startIcon={<PersonAdd />} onClick={() => setInviteOpen(true)}>
          Invite User
        </Button>
      </Box>

      {isFirstUser && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No users yet. The first user you invite becomes the TenantAdmin.
        </Alert>
      )}

      <Paper sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
        ) : users.length === 0 ? (
          <Typography sx={{ p: 3 }} color="text.secondary" variant="body2">
            No users belong to this tenant yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(u => (
                <TableRow key={u._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                        {(u.firstName?.[0] ?? u.email[0] ?? '?').toUpperCase()}
                      </Avatar>
                      {`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || '—'}
                    </Box>
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    {u.roles.map(r => (
                      <Chip key={r} label={r} size="small" sx={{ mr: 0.5 }}
                        color={r === 'TenantAdmin' ? 'primary' : 'default'} variant="outlined" />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip label={u.status} size="small"
                      color={u.status === 'Active' ? 'success' : u.status === 'Suspended' ? 'error' : 'default'}
                      variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit roles">
                      <IconButton size="small" onClick={() => { setRoleEditUser(u); setRoleEditValue([...u.roles]); }}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {u.status !== 'Suspended' && (
                      <Tooltip title="Deactivate">
                        <IconButton size="small" color="error" disabled={deactivatingId === u._id}
                          onClick={() => handleDeactivate(u)}>
                          {deactivatingId === u._id ? <CircularProgress size={16} /> : <Block fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isFirstUser ? 'Invite First User (TenantAdmin)' : 'Invite User'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {isFirstUser && (
            <Alert severity="info">This is the first user for this tenant — they will be assigned the TenantAdmin role.</Alert>
          )}
          <TextField label="Email" type="email" required value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)} fullWidth size="small" />
          <TextField label="First Name" value={inviteFirstName}
            onChange={e => setInviteFirstName(e.target.value)} fullWidth size="small" />
          <TextField label="Last Name" value={inviteLastName}
            onChange={e => setInviteLastName(e.target.value)} fullWidth size="small" />
          {!isFirstUser && (
            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={inviteRole} label="Role" onChange={e => setInviteRole(e.target.value)}>
                {ROLE_OPTIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={inviteSaving}>Cancel</Button>
          <Button onClick={handleInvite} variant="contained" disabled={inviteSaving}>
            {inviteSaving ? <CircularProgress size={16} /> : 'Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!roleEditUser} onClose={() => setRoleEditUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Roles — {roleEditUser?.email}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Roles</InputLabel>
            <Select multiple value={roleEditValue} label="Roles"
              onChange={e => setRoleEditValue(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}>
              {ROLE_OPTIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleEditUser(null)} disabled={roleSaving}>Cancel</Button>
          <Button onClick={handleRolesSave} variant="contained" disabled={roleSaving || roleEditValue.length === 0}>
            {roleSaving ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
